import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil, filter } from 'rxjs/operators';
import { DownloadMatchService } from '../../../scoreboard/download-match/download-match.service';
import { Match } from '../../../_shared/models/match.model';
import { Tournament } from '../../../_shared/models/tournament.model';
import { TournamentDetailsService } from '../tournament-details.service';
import { Socket } from 'ngx-socket-io';
import { MatchService } from '../../../_core/services/match.service';
import { MatchUpStatusEnum } from '../../../_shared/models/csTypes/enums/matchup_status';
import { InfiniteScrollCustomEvent, ModalController } from '@ionic/angular';
import { Location } from '@angular/common';
import { CreateMatchComponent } from '../../../matches/create-match/create-match.component';
import { AuthService } from '../../../auth/auth.service';
import { TrackerBarService } from '../../../scoreboard/tracker-bar/tracker-bar.service';
import { ToastService } from '../../../_core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Router, NavigationEnd } from '@angular/router';
import { Venue } from '../../../_shared/models/csTypes/venue';

interface Filter {
    matchUpStatus?: any;
    matchUpStatusCodes?: string[];
    dates?: Date[];
    sort?: number;
    page?: number;
    limit?: number;
}

@Component({
    standalone: false,
    selector: 'app-live-score',
    templateUrl: './live-score.component.html',
    styleUrls: ['./live-score.component.scss'],
})
export class LiveScoreComponent implements OnInit, OnDestroy {
    private authService = inject(AuthService);
    private translateService = inject(TranslateService);

    matchUps: Match[];
    matchUps$: Observable<any>;
    loadedMatchUps: Match[];
    filteredMatchUps: Match[];
    page = 1;
    limit = 20;
    matchType: string;
    tournament: Tournament;
    selectedDay: any;

    isLoadingMatches = false;
    isLoadingMore = false;
    canLoadMoreMatches = false;
    canLoadMoreTmxMatches = false;
    infiniteScrollEvent: InfiniteScrollCustomEvent | null = null;
    infiniteScrollTimeout: any = null;

    private destroyed$: Subject<void> = new Subject();
    isTracker: any;
    isOwner: any;
    allowSkipCache: boolean;

    isThemedStreamView: boolean;
    redirectionCompleted = false;
    partner: string = '';
    venueId: string = '';
    date: string = '';
    venues: Venue[] = [];

    constructor(
        private tournamentDetailsService: TournamentDetailsService,
        private downloadMatchService: DownloadMatchService,
        private trackerBarService: TrackerBarService,
        private matchService: MatchService,
        private activatedRoute: ActivatedRoute,
        private socket: Socket,
        public location: Location,
        private modalController: ModalController,
        private toastService: ToastService,
        private router: Router
    ) { }

    async ngOnInit() {
        this.isThemedStreamView = this.location.path().includes('stream-view');

        // If it is displayed on a tournament board refresh every 5 min to pickup new matches
        if (this.isThemedStreamView) {
            setInterval(async () => {
                await this.getMatches(false, false, true)
            }, 3 * 60 * 1000);
        }

        this.matchType = this.activatedRoute.snapshot.data['type'];
        await this.getTournamentInfo();
        this.subscribe();

        if (this.router.url.includes('live-score')) {
            this.router.events
                .pipe(
                    filter(event => event instanceof NavigationEnd),
                    takeUntil(this.destroyed$),
                )
                .subscribe(event => {
                    if (this.router.url.includes('live-score')) {
                        this.getMatches(true, true, true);
                    }
                });
        }
    }

    ngOnDestroy() {
        this.socket.emit('leave-room', this.tournament?._id);
        this.socket.removeListener('tournament-match-added');
        if (this.infiniteScrollTimeout) {
            clearTimeout(this.infiniteScrollTimeout);
        }
        this.destroyed$.next();
        this.destroyed$.complete();
        this.tournamentDetailsService.setSelectedTournamentDate("");
    }

    ionViewWillEnter() {
        this.tournamentDetailsService.publishClearBadge(this.matchType);
    }

    async getTournamentInfo() {
        const tournamentId = this.activatedRoute.snapshot.parent.params.id;
        this.tournament = await this.tournamentDetailsService.getTournamentInfo(tournamentId);
        this.partner = this.tournament?.partner || '';
        if (this.tournament) {
            this.isOwner = this.tournament.owners.some(o => this.authService.authUserSignal()?._id === o);
            this.isTracker = this.tournament.trackers.some(t => this.authService.authUserSignal()?._id === t.tracker._id);
        }
    }

    async changeDate(data: { date: string, fromCache?: boolean }) {
        if (!this.tournament) {
            await this.getTournamentInfo();
        }
        const { date, fromCache } = data;
        this.date = date;
        this.selectedDay = date;
        const skipCache = fromCache ? false : (this.isOwner || this.isTracker) ? true : false;
        await this.getMatches(true, true, skipCache);
    }

    async changeVenue(venueId: string) {
        this.venueId = venueId;
        await this.getMatches(true, true, true);
    }

    async redirectBasedOnTournamentStatus(hasMatches: boolean = true) {
        const now = new Date().getTime();
        const start = new Date(this?.tournament?.startDate ?? '').getTime();
        const end = new Date(this?.tournament?.endDate ?? '').getTime();

        let targetRoute = 'live-score';

        if (hasMatches) {
            targetRoute = 'live-score'
        } else if (now < start) {
            // Not started yet
            targetRoute = 'upcoming';
        } else if (now > end) {
            // Finished
            targetRoute = 'completed';
        } else {
            targetRoute = 'completed';
        }

        const current = this.router.url;
        const lastIndex = current.lastIndexOf('live-score');
        const next = lastIndex !== -1
            ? current.substring(0, lastIndex) + targetRoute + current.substring(lastIndex + 'live-score'.length)
            : current;
        this.router.navigateByUrl(next);
        this.redirectionCompleted = true;
    }

    async getMatches(showSkeleton: boolean, reset, skipCache: boolean, ev?) {
        this.isLoadingMatches = showSkeleton;

        if (!skipCache) {
            this.allowSkipCache = false;
        } else if (this.isOwner || this.isTracker || this.authService.authUserSignal()?.role?.value === 500) {
            this.allowSkipCache = true;
        }

        const tournamentId = this.activatedRoute.snapshot.parent.params.id;
        this.tournament = await this.tournamentDetailsService.getTournamentInfo(tournamentId);

        if (reset) {
            this.matchUps = [];
            this.loadedMatchUps = [];
            this.page = 1;
            this.venues = [];
        }

        let filter = this.getMatchTypeFilter(this.matchType)

        if (this.partner?.toLowerCase() === 'tmx' && this.matchType === 'upcoming') {
            let venueId = this.venueId === 'all' ? '' : (this.venueId || "");
            
            this.matchService
            .getTournamentMatchesByPartner(this.partner?.toLowerCase(), tournamentId, this.date, venueId, this.page, this.limit)
            .pipe(
                takeUntil(this.destroyed$),
                finalize(async () => {
                    this.isLoadingMatches = false;
                    this.isLoadingMore = false;
                    if (this.infiniteScrollEvent) {
                        this.infiniteScrollEvent.target.complete();
                        if (this.infiniteScrollTimeout) {
                            clearTimeout(this.infiniteScrollTimeout);
                        }
                        this.infiniteScrollTimeout = setTimeout(() => {
                            this.infiniteScrollEvent = null;
                            this.infiniteScrollTimeout = null;
                        }, 300);
                    }
                    if (ev) {
                        ev.target.complete();
                    }
                })
            )
            .subscribe(
                async res => {
                    const data = res?.data?.venues || [];
                    if (reset) {
                        this.venues = data;
                    } else {
                        data.forEach((newVenue: Venue) => {
                            const existingVenue = this.venues.find(v => v.venueId === newVenue.venueId || v.venueName === newVenue.venueName);
                            if (existingVenue) {
                                newVenue.courts?.forEach(newCourt => {
                                    const existingCourt = existingVenue.courts?.find(c => c.courtId === newCourt.courtId || c.courtName === newCourt.courtName);
                                    if (existingCourt) {
                                        existingCourt.matchUps = [...(existingCourt.matchUps || []), ...(newCourt.matchUps || [])];
                                    } else {
                                        existingVenue.courts = [...(existingVenue.courts || []), newCourt];
                                    }
                                });
                            } else {
                                this.venues.push(newVenue);
                            }
                        });
                    }
                
                    this.canLoadMoreTmxMatches = res.data.pagination.hasNextPage;
                }
            );
        } else {

        this.tournamentDetailsService
            .getMatches(this.tournament._id, this.matchType, filter, this.allowSkipCache)
            .pipe(
                takeUntil(this.destroyed$),
                finalize(async () => {
                    this.isLoadingMatches = false;
                    this.isLoadingMore = false;
                    if (this.infiniteScrollEvent) {
                        this.infiniteScrollEvent.target.complete();
                        if (this.infiniteScrollTimeout) {
                            clearTimeout(this.infiniteScrollTimeout);
                        }
                        this.infiniteScrollTimeout = setTimeout(() => {
                            this.infiniteScrollEvent = null;
                            this.infiniteScrollTimeout = null;
                        }, 300);
                    }
                    if (ev) {
                        ev.target.complete();
                    }
                })
            )
            .subscribe(
                async res => {
                    // await this.handleMatchesInitialLoad(res.data);

                    const hasMatches = Array.isArray(res?.data?.matches)
                        ? res.data.matches.length > 0
                        : true;

                    if (!this.redirectionCompleted) {
                        await this.redirectBasedOnTournamentStatus(hasMatches);
                    }

                    this.sortMatches(this.sortMatchesByCourt(res.data.matches));

                    if (this.isThemedStreamView) {
                        // If it is on a tournament matches, fetch first x matches
                        // And remove tab bar
                        this.matchUps = this.sortMatchesByCourt(res.data.matches) || [];

                        const tabBar = document.getElementById('app-tab-bar');
                        if (tabBar !== null) {
                            tabBar.style.display = 'none';
                        }
                    } else {
                        // Else load tournament matches
                        this.matchUps.push(...this.sortMatchesByCourt(res.data.matches));
                        this.loadedMatchUps.push(...this.sortMatchesByCourt(res.data.matches));
                        this.filteredMatchUps = this.matchUps;
                        this.canLoadMoreMatches = res.data.pagination?.totalRecords > this.matchUps.length ? true : false;
                    }
                }
            );
        }
    }


    loadMoreTmxMatches(infiniteScroll: InfiniteScrollCustomEvent) {
        if (!this.canLoadMoreTmxMatches || this.isLoadingMatches || this.isLoadingMore || this.infiniteScrollEvent) {
            infiniteScroll.target.complete();
            return;
        }
        this.isLoadingMore = true;
        this.infiniteScrollEvent = infiniteScroll;
        this.page++;
        this.getMatches(false, false, true);
    }

    private getMatchTypeFilter(matchType: string) {
        if (matchType === 'live-score') {
            return {
                matchUpStatus: [MatchUpStatusEnum.IN_PROGRESS, 'TBP', MatchUpStatusEnum.SUSPENDED],
                matchUpStatusCodes: ['LIVE_SCORE'],
                sort: 1,
                page: this.page
            };
        } else if (matchType === 'upcoming') {
            return {
                matchUpStatus: ['TBP', MatchUpStatusEnum.IN_PROGRESS, MatchUpStatusEnum.COMPLETED, MatchUpStatusEnum.RETIRED, MatchUpStatusEnum.INCOMPLETE],
                sort: 1,
                dates: [this.selectedDay],
                page: this.page
            };
        } else if (matchType === 'completed') {
            return {
                matchUpStatus: [MatchUpStatusEnum.COMPLETED, MatchUpStatusEnum.RETIRED, MatchUpStatusEnum.INCOMPLETE],
                sort: 1,
                dates: [this.selectedDay],
                page: this.page
            };
        }
    }

    private replaceImportedMatchUpWithDownloadedMatchUp(toRemoveMatch: Match) {
        // Update filtered and full (loaded) list
        const index = this.matchUps.findIndex(matchUp => matchUp.matchId === toRemoveMatch.matchId);
        if (index !== -1) {
            this.matchUps.splice(index, 1, toRemoveMatch);
        }

        const loadedIndex = this.loadedMatchUps.findIndex(matchUp => matchUp.matchId === toRemoveMatch.matchId);
        if (loadedIndex !== -1) {
            this.loadedMatchUps.splice(loadedIndex, 1, toRemoveMatch);
        }
    }

    private replaceImportedTmxMatchWithDownloadedMatch(downloadedMatch: Match) {
        if (!downloadedMatch?.matchId) return;
      
        const matchId = downloadedMatch.matchId;
      
        this.venues?.forEach(v => v?.courts?.forEach(c =>
            this.updateMatchInList(c.matchUps, matchId, downloadedMatch)
          )
        );
      
        this.updateMatchInList(this.matchUps, matchId, downloadedMatch);
        this.updateMatchInList(this.loadedMatchUps, matchId, downloadedMatch);
        this.updateMatchInList(this.filteredMatchUps, matchId, downloadedMatch);
    }

    private removeMatchFromMatchUpList(toRemoveMatch: Match) {
        const index = this.matchUps.findIndex(matchUp => matchUp.matchId === toRemoveMatch.matchId);
        if (index !== -1) {
            this.matchUps.splice(index, 1);
        }

        const loadedIndex = this.loadedMatchUps.findIndex(matchUp => matchUp.matchId === toRemoveMatch.matchId);
        if (loadedIndex !== -1) {
            this.loadedMatchUps.splice(loadedIndex, 1);
        }
    }

    private removeTmxMatchFromMatchUpList(toRemoveMatch: Match) {
        if (!toRemoveMatch?.matchId) return;
      
        const matchId = toRemoveMatch.matchId;
      
        this.venues?.forEach(v => v?.courts?.forEach(c => {
            this.updateMatchInList(c.matchUps, matchId);
            if (c.matchUps?.length === 0) c.matchUps = [];
          })
        );
      
        this.updateMatchInList(this.matchUps, matchId);
        this.updateMatchInList(this.loadedMatchUps, matchId);
        this.updateMatchInList(this.filteredMatchUps, matchId);
    }

    private updateMatchInList( list: any[] | undefined, matchId: string, newMatch?: Match ) {
        if (!list) return;
      
        const index = list.findIndex(m => m?.matchId === matchId);
        if (index === -1) return;
      
        if (newMatch) {
          list.splice(index, 1, newMatch);
        } else {
          list.splice(index, 1);
        }
    }

    private sortMatches(matchUps: Match[]): Match[] {
        if (this.matchType === 'live-score') {
            matchUps.sort((a, b) => {
                const indexA = a.extensions.findIndex(ext => ext.name === 'court');
                const indexB = b.extensions.findIndex(ext => ext.name === 'court');

                if (indexA === -1) return 1;
                if (indexB === -1) return -1;

                if (a.extensions[indexA]?.value < b.extensions[indexB]?.value) { return -1; }
                else if (a.extensions[indexB]?.value > b.extensions[indexB]?.value) { return 1; }
                return 0;
            });
            return;
        } else if (this.matchType === 'upcoming') {
            // Sort matches by time
            matchUps.sort((a, b) => {
                if (!a.schedule) return -1;
                if (!b.schedule) return 1;

                a.schedule.scheduledTime = a.schedule.scheduledTime?.includes('T') ? a.schedule.scheduledTime.split('T')[1] : a.schedule.scheduledTime;
                b.schedule.scheduledTime = b.schedule.scheduledTime?.includes('T') ? b.schedule.scheduledTime.split('T')[1] : b.schedule.scheduledTime;

                if (a.schedule.scheduledTime < b.schedule.scheduledTime) { return -1; }
                else if (a.schedule.scheduledTime > b.schedule.scheduledTime) { return 1; }
                else return 0;
            });
        } else {
            matchUps.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        }

        return matchUps;
    }

    private getMatchUpdate() {
        const observable = new Observable(observer => {
            this.socket.on('tournament-match-added', data => {
                observer.next(data);
            });
        });
        return observable;
    }

    onSearchChange(ev) {
        // Reset items back to all of the items
        this.initializeItems();

        // set val to the value of the ev target
        const val = ev.target.value;

        // if the value is an empty string don't filter the items
        if (val && val.trim() !== '') {
            this.filteredMatchUps = this.filteredMatchUps.filter((matchUp) => {
                return (
                    matchUp.sides[0]?.players[0]?.participant?.first_name?.toLowerCase().indexOf(val.toLowerCase()) > -1 ||
                    matchUp.sides[0]?.players[1]?.participant?.first_name?.toLowerCase().indexOf(val.toLowerCase()) > -1 ||
                    matchUp.sides[1]?.players[0]?.participant?.first_name?.toLowerCase().indexOf(val.toLowerCase()) > -1 ||
                    matchUp.sides[1]?.players[1]?.participant?.first_name?.toLowerCase().indexOf(val.toLowerCase()) > -1 ||
                    matchUp.sides[0]?.players[0]?.participant?.last_name?.toLowerCase().indexOf(val.toLowerCase()) > -1 ||
                    matchUp.sides[0]?.players[1]?.participant?.last_name?.toLowerCase().indexOf(val.toLowerCase()) > -1 ||
                    matchUp.sides[1]?.players[0]?.participant?.last_name?.toLowerCase().indexOf(val.toLowerCase()) > -1 ||
                    matchUp.sides[1]?.players[1]?.participant?.last_name?.toLowerCase().indexOf(val.toLowerCase()) > -1
                );
            });
        }

        this.matchUps = this.filteredMatchUps;
    }

    initializeItems() {
        this.filteredMatchUps = this.loadedMatchUps;
    }

    async addMatchUp() {
        const modal = await this.modalController.create({
            component: CreateMatchComponent,
            componentProps: {
                tournament: this.tournament
            }
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data) {
            this.matchUps.unshift(data.match);
        }

    }

    private async subscribe() {
        if (this.matchType === 'upcoming') {
            this.downloadMatchService
                .subscribeFollow()
                .pipe(takeUntil(this.destroyed$))
                .subscribe(
                    (resMatchUp: Match) => {
                        if (this.partner?.toLowerCase() === 'tmx') {
                            this.replaceImportedTmxMatchWithDownloadedMatch(resMatchUp);
                        } else {
                            this.replaceImportedMatchUpWithDownloadedMatchUp(resMatchUp);
                        }
                    }
                );
        }

        this.trackerBarService
            .subscribeStartLiveScore()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                (matchUp: Match) => {
                    if (this.matchType === 'live-score') {
                        this.matchUps.unshift(matchUp);
                    }
                    if (this.matchType === 'upcoming') {
                        if (this.partner?.toLowerCase() === 'tmx') {
                            this.removeTmxMatchFromMatchUpList(matchUp);
                        } else {
                            this.removeMatchFromMatchUpList(matchUp);
                        }
                    }
                    this.toastService.presentToast(this.translateService.instant('Match is moved to in progress tab'))
                }
            );

        // If a match is completed remove it from live score list
        this.matchService
            .subscribeMatchCompleted()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                (completedMatchUp: Match) => {
                    if (this.matchType === 'live-score') {
                        this.removeMatchFromMatchUpList(completedMatchUp);
                    }
                });

        // If a match is deleted remove from the match list
        this.matchService
            .subscribeDelete()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                (deletedMatchUp: Match) => {
                    this.removeMatchFromMatchUpList(deletedMatchUp);
                });

        // If a match is completed during live scoring get that the match is completed
        // And move to Results tab
        this.matchService
            .subscribeEdit()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                (updatedMatchUp: Match) => {
                    if (this.partner?.toLowerCase() === 'tmx' && this.matchType === 'upcoming') {
                        this.replaceImportedTmxMatchWithDownloadedMatch(updatedMatchUp);
                    } else if (updatedMatchUp.matchStatus === MatchUpStatusEnum.COMPLETED &&
                        this.matchType === 'completed' &&
                        !this.matchUps.some(matchUp => matchUp.matchId === updatedMatchUp.matchId)) {
                        this.matchUps.unshift(updatedMatchUp);
                    }
                });
    }

    getMatchesAndSkipCache() {
        this.getMatches(true, true, true);
    }


    sortMatchesByCourt(matches: Match[], orderBy: string = 'asc'): Match[] {
        const direction = orderBy.toLowerCase() === 'desc' ? -1 : 1;

        return [...matches].sort((a: Match, b: Match) => {
            const getVenueName = (match: Match): string =>
                match?.schedule?.venue?.venueName?.toLowerCase() || '';

            const getCourtName = (match: Match): string =>
                match?.schedule?.court?.courtName?.toLowerCase() || '';

            const getCourtNumber = (match: Match): number | null => {
                const name = getCourtName(match);
                const num = parseInt(name.replace(/[^\d]/g, ''));
                return isNaN(num) ? null : num;
            };

            // Venue comparison
            const venueA = getVenueName(a);
            const venueB = getVenueName(b);
            const aHasVenue = !!venueA;
            const bHasVenue = !!venueB;

            if (!aHasVenue && bHasVenue) return 1;
            if (aHasVenue && !bHasVenue) return -1;
            if (!aHasVenue && !bHasVenue) return 0;

            const venueCompare = venueA.localeCompare(venueB);
            if (venueCompare !== 0) return venueCompare * direction;

            // Venue same — apply court name/number logic
            const courtNumA = getCourtNumber(a);
            const courtNumB = getCourtNumber(b);

            const courtNameA = getCourtName(a);
            const courtNameB = getCourtName(b);

            const aHasNum = courtNumA !== null;
            const bHasNum = courtNumB !== null;

            // Prioritize non-numeric court names (alphabetical) over numeric ones
            if (!aHasNum && bHasNum) return -1;
            if (aHasNum && !bHasNum) return 1;
            if (!aHasNum && !bHasNum) {
                return courtNameA.localeCompare(courtNameB) * direction;
            }

            // Both numeric
            return (courtNumA! - courtNumB!) * direction;
        });
    }
}
